// frontend/packages/business-console/src/pages/ContentWorkspace.jsx
//
// Consolidated Content surface. Per the AI + automation plan (3.1.4 in
// docs/design/ai-automation-opportunities.md) and the cockpit single-surface
// principle, the file editor and the AI writer live behind ONE "Content" nav
// entry as tabs, instead of a separate top-level "AI Blog Writer" button.
//
// Both children render `embedded` so they drop their own page header and share
// this one. The AI tab is deep-linkable with `?tab=ai`; the legacy
// `/blog/ai-writer` route still resolves to the standalone writer.
//
// The "Ask our team" tab (`?tab=requests`, doc 28 W14) sends a content
// request to the UpRyzze team's task queue and lists its status.

import React, { useMemo } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';

import ContentEditor from './ContentEditor';
import AiWriterPanel from './AiWriterPanel';
import ContentRequestsPanel from '../components/content/ContentRequestsPanel';
import { PageHeader } from '../components/primitives';

export default function ContentWorkspace() {
  const location = useLocation();
  const navigate = useNavigate();

  const tab = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const t = params.get('tab');
    return t === 'ai' || t === 'requests' ? t : 'files';
  }, [location.search]);

  const handleChange = (_e, next) => {
    const params = new URLSearchParams(location.search || '');
    if (next === 'ai' || next === 'requests') params.set('tab', next);
    else params.delete('tab');
    const qs = params.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : '' },
      { replace: false }
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { md: '100%' },
        minHeight: 0,
        width: '100%',
      }}
    >
      <Box sx={{ px: 3, pt: 2.5 }}>
        <PageHeader title="Content" />
        <Tabs
          value={tab}
          onChange={handleChange}
          sx={{ mt: 1, minHeight: 0 }}
          aria-label="Content tabs"
        >
          <Tab
            value="files"
            icon={<EditOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Pages & posts"
            sx={{ minHeight: 44, textTransform: 'none' }}
          />
          <Tab
            value="ai"
            icon={<AutoAwesomeRoundedIcon fontSize="small" />}
            iconPosition="start"
            label="AI Writer"
            sx={{ minHeight: 44, textTransform: 'none' }}
          />
          <Tab
            value="requests"
            icon={<SupportAgentOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Ask our team"
            sx={{ minHeight: 44, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      <Box
        sx={{
          flex: { md: 1 },
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {tab === 'ai' ? (
          <AiWriterPanel />
        ) : tab === 'requests' ? (
          <ContentRequestsPanel />
        ) : (
          <ContentEditor embedded />
        )}
      </Box>
    </Box>
  );
}
